import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { filter, map, Observable } from 'rxjs';

import { OperationRoutes } from '@ripeurs/common/types/gateway';

import { ApiV2Service } from '~app/services/api-v2.service';
import { OperationType } from '~type/service/utility/operation';


@Injectable()
export class UtilityOperationStepHandler {
	constructor(private readonly _api: ApiV2Service) {}

	public putCustomerStatus({ id }: { id: string }) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: { status: OperationType.Status.CUSTOMER_CONTACTED },
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public putTimerStatus({
		id,
		status,
		time,
		type,
	}: {
		id: string,
		status: OperationType.Status.TRAVEL_TIME_ADDED | OperationType.Status.OPERATION_TIME_ADDED,
		type: 'AUTO' | 'MANUAL',
		time: number,
	}) {
		const meta = {
			times: {
				operation: status === OperationType.Status.OPERATION_TIME_ADDED ? { type, time } : undefined,
				travel: status === OperationType.Status.TRAVEL_TIME_ADDED ? { type, time } : undefined,
			},
		};

		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: { status: status, meta },
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public postPicturesFile({
		id,
		file,
		category,
	}: {
		id: OperationRoutes.PostOperationFile.Def['request']['params']['id'],
		file: File | Blob,
		category: OperationRoutes.PostOperationFile.Def['request']['body']['category'],
	}): Observable<(
		| { progress: number, file: null, alreadyUploaded?: undefined }
		|
		{
			progress: null,
			file: { sha256: OperationType.File.OperationFile['sha256'], category: OperationType.File.Category },
			alreadyUploaded: boolean,
		}
		)> {
		const formData = new FormData;
		formData.append('file', file);
		formData.append('category', category);

		return this._api.request<OperationRoutes.PostOperationFile.Def>(
			OperationRoutes.PostOperationFile.config,
			{
				params: { id },
				body: formData as unknown as OperationRoutes.PostOperationFile.Def['request']['body'],
			},
			{
				reportProgress: true,
				loaderHeader: false,
			},
		).pipe(
			filter((event: any) => {
				return event instanceof HttpResponse || (
					typeof event === 'object'
					&& event !== null
					&& event.type === 1
				);
			}),
			map((event: any) => {
				if (!(event instanceof HttpResponse)) {
					return { progress: event.total ? event.loaded / event.total : 0, file: null };
				}
				const result: OperationRoutes.PostOperationFile.Def['response'] = event.body;
				if (result.code !== 'UPLOADED' && result.code !== 'ALREADY_UPLOADED') {
					alert(result.code);
					throw new Error(result.code);
				}
				return {
					progress: null,
					file: result.data.file,
					alreadyUploaded: result.code === 'ALREADY_UPLOADED',
				};
			}),
		);
	}

	public putPicturesStatus({
		id,
		status,
	}: {
		id: string,
		status:
			| OperationType.Status.OPERATION_PICTURES_TAKED_BEFORE
			| OperationType.Status.OPERATION_PICTURES_TAKED_AFTER_OPTIONAL
			| OperationType.Status.OPERATION_PICTURES_TAKED_AFTER
			| OperationType.Status.TRUCK_PICTURES_TAKED,
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: { status },
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public putForecastStatus({
		id,
		meta,
	}: {
		id: OperationType.Operation['id'],
		meta: {
			forecasts: {
				operation: OperationType.Operation['meta']['forecasts']['operation'],
			},
		},
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: {
				id: id,
			},
			body: {
				status: OperationType.Status.OPERATION_FORECAST_CHECKED,
				meta: meta,
			},
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public putContentStatus({
		id,
		content,
		comment,
		meta,
	}: {
		id: string,
		content: OperationType.Contents.Type,
		comment: OperationType.Operation['comment'],
		meta: {
			rep: OperationType.Operation['meta']['rep'],
		}
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: {
				id: id,
			},
			body: {
				status: OperationType.Status.OPERATION_CONTENT_ADDED,
				content: content,
				comment: comment,
				meta: meta,
			},
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public getPrice({
		id,
		content,
	}: {
		id: OperationRoutes.GetOperationPrice.Def['request']['params']['id'],
		content?: OperationRoutes.GetOperationPrice.Def['request']['query']['content'],
	}) {
		return this._api.request<OperationRoutes.GetOperationPrice.Def>(OperationRoutes.GetOperationPrice.config, {
			params: { id },
			query: { content },
		}).pipe(map((result) => {
			if (result.code !== 'OK') {
				alert(result.code);
				throw new Error(result.code);
			}
			return {
				price: result.data.price,
				zoneId: result.data.zoneId,
			};
		}));
	}

	public putPricingStatus({
		id,
		contentOptions,
	}: {
		id: string,
		contentOptions: OperationType.Contents.Type['options'],
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: {
				status: OperationType.Status.OPERATION_PRICE_ADDED,
				content: { options: contentOptions },
			},
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public putValidatedStatus({
		id,
		frontPriceFinal,
	}: {
		id: string,
		frontPriceFinal: number,
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: {
				status: OperationType.Status.OPERATION_VALIDATED,
				frontPriceFinal: frontPriceFinal,
			},
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}

	public putHandlerStatus({
		id,
		handler,
	}: {
		id: OperationType.Operation['id'],
		handler: {
			count: OperationType.Operation['meta']['handler']['count'],
			comment: OperationType.Operation['meta']['handler']['comment'],
			leftovers: OperationType.Operation['meta']['handler']['leftovers'],
		},
	}) {
		return this._api.request<OperationRoutes.PutOperation.Def>(OperationRoutes.PutOperation.config, {
			params: { id },
			body: {
				status: OperationType.Status.INTERNAL_META_ADDED,
				meta: {
					handler: {
						count: handler.count,
						comment: handler.comment,
						leftovers: handler.leftovers,
					},
				},
			},
		}).pipe(map((result) => {
			if (result.code !== 'UPDATED') {
				alert(result.code);
				throw new Error(result.code);
			}
			return result.code;
		}));
	}
}
